<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'SupportTickets' }" class="btn btn-secondary">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </router-link>
      </div>
    </div>

    <div class="page-container">
      <form class="ticket-form" @submit.prevent="handleSubmit">
        <div class="form-section">
          <h2 class="section-title">工單內容</h2>

          <div class="form-group">
            <label class="form-label required">主題</label>
            <input
              v-model="form.subject"
              type="text"
              class="form-control"
              maxlength="200"
              placeholder="簡述問題"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label required">說明</label>
            <textarea
              v-model="form.description"
              class="form-control"
              rows="6"
              placeholder="詳細描述問題或需求"
              required
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">分類</label>
              <select v-model="form.categoryId" class="form-control">
                <option value="">不指定</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                  {{ cat.name }}
                </option>
              </select>
              <p class="field-hint">
                可至
                <router-link :to="{ name: 'SupportTicketCategories' }">工單分類</router-link>
                自行維護
              </p>
            </div>
            <div class="form-group">
              <label class="form-label">優先級</label>
              <select v-model="form.priority" class="form-control">
                <option value="low">低</option>
                <option value="medium">中</option>
                <option value="high">高</option>
                <option value="urgent">緊急</option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h2 class="section-title">關聯會員（選填）</h2>
          <MemberSearch v-model="selectedMembers" :max-selections="1" />
        </div>

        <div class="form-actions">
          <button type="button" class="btn btn-secondary" :disabled="submitting" @click="goBack">
            取消
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <i class="ri-save-line"></i>
            {{ submitting ? '建立中...' : '建立工單' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { supportService } from '@/api/modules/support.js';
import MemberSearch from '@/components/MemberSearch.vue';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'SupportTicketCreate',
  components: { MemberSearch },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('新增客服工單');
    const submitting = ref(false);
    const selectedMembers = ref([]);
    const categories = ref([]);
    const form = reactive({
      subject: '',
      description: '',
      categoryId: '',
      priority: 'medium'
    });

    const goBack = () => {
      router.push({ name: 'SupportTickets' });
    };

    const loadCategories = async () => {
      try {
        const response = await supportService.listTicketCategories({ activeOnly: true });
        categories.value = response?.data || [];
      } catch (err) {
        console.warn('載入分類失敗', err);
      }
    };

    const handleSubmit = async () => {
      if (!form.subject.trim() || !form.description.trim()) {
        await showError('請填寫必填欄位', '主題與說明為必填');
        return;
      }

      submitting.value = true;
      try {
        const payload = {
          subject: form.subject.trim(),
          description: form.description.trim(),
          priority: form.priority
        };
        if (form.categoryId) payload.categoryId = form.categoryId;
        if (selectedMembers.value.length > 0) {
          payload.memberId = selectedMembers.value[0].id;
        }

        const response = await supportService.createTicket(payload);
        if (response?.success && response.data?.id) {
          await showSuccess('建立成功', '工單已建立');
          router.push({ name: 'SupportTicketDetail', params: { id: response.data.id } });
        } else {
          await showError('建立失敗', response?.message || '無法建立工單');
        }
      } catch (err) {
        console.error('建立工單失敗:', err);
        await showError('建立失敗', err.response?.data?.message || err.message || '無法建立工單');
      } finally {
        submitting.value = false;
      }
    };

    onMounted(loadCategories);

    return {
      pageTitle,
      form,
      categories,
      selectedMembers,
      submitting,
      handleSubmit,
      goBack
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.ticket-form {
  max-width: 800px;
}

.form-section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-label.required::after {
  content: ' *';
  color: #e03131;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 8px;
}

.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #868e96;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
